import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import { withRouter } from "../../utils/withRouter";
import Swal from "sweetalert2";

class OrderPayments extends Component {

  state = {
    order: null,
    payments: [],
    loading: true,

    paidInput: "",
    method: "Cash",
    note: "",

    submitting: false,
  };

  async componentDidMount() {
    const { id } = this.props.router.params;
    await this.fetchOrderDetails(id);
    await this.fetchPaymentsDetails(id);
  }

  // -------------------------
  // Fetch Order
  // -------------------------
  fetchOrderDetails = async (id) => {
    try {
      const res = await axios.get(`/products/orders/${id}`);

      if (res.data.status !== "success") {
        Swal.fire("Error", "Order not found", "error");
        return;
      }

      const data = res.data.data;
      const order = data.order || data[0];

      this.setState({
        order,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load order", "error");
    }
  };

  // -------------------------
  // Fetch Payments
  // -------------------------
  fetchPaymentsDetails = async (id) => {
    try {
      const res = await axios.get(`/products/orders/payments/${id}`);

      if (res.data.status !== "success") {
        Swal.fire("Error", "Failed to load payments", "error");
        return;
      }

      const data = res.data.data || res.data;
      const payments = data.payments || data || [];

      this.setState({
        payments,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load payments", "error");
    }
  };

  // -------------------------
  // Status Helper
  // -------------------------
  getPaymentStatus = () => {
    const { order } = this.state;

    if (!order) return "Unpaid";

    if (Number(order.due_amount) === 0) return "Paid";
    if (Number(order.paid_amount) > 0) return "Partial";
    return "Unpaid";
  };

  // -------------------------
  // Confirm Payment
  // -------------------------
  handleConfirmPayment = async () => {

    const { paidInput, note, method, order } = this.state;

    if (!order) return;

    const due = Number(order.due_amount);

    if (!paidInput || Number(paidInput) <= 0) {
      return Swal.fire("Warning", "Enter valid payment amount", "warning");
    }

    if (Number(paidInput) > due) {
      return Swal.fire("Warning", "Amount exceeds due", "warning");
    }

    try {
      this.setState({ submitting: true });

      const res = await axios.post(
        `/products/orders/payments/${order.id}`,
        {
          order_id: order.id,
          payment_amount: paidInput,
          payment_note: note,
          payment_method: method,
        }
      );

      if (res.data.status !== "success") {
        throw new Error("Payment failed");
      }

      Swal.fire("Success", "Payment recorded", "success");

      this.setState({
        paidInput: "",
        note: "",
      });

      await this.fetchOrderDetails(order.id);
      await this.fetchPaymentsDetails(order.id);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to save payment", "error");
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleDelete = async (paymentId) => {
    const { order } = this.state;
    if (!order) return;

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This payment will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.delete(
        `/products/orders/payments/${paymentId}`
      );

      if (res.data.status === "success") {
        Swal.fire("Deleted!", res.data.message, "success");

        // ✅ REFRESH UI STATE
        await this.fetchOrderDetails(order.id);
        await this.fetchPaymentsDetails(order.id);
      } else {
        Swal.fire("Error", "Delete failed", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete payment", "error");
    }
  };


  // -------------------------
  // Render
  // -------------------------
  render() {
    const {
      order,
      loading,
      payments,
      paidInput,
      method,
      note,
      submitting,
    } = this.state;

    if (loading) return <div className="p-4">Loading...</div>;

    if (!order)
      return <div className="p-4 text-danger">Order not found</div>;

    const status = this.getPaymentStatus();

    return (
      <div className="pc-container">
        <div className="pc-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold">Order Payments</h4>
          </div>

          {/* Summary */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card p-3">
                <div className="small text-muted">Grand Total</div>
                <div className="fw-bold fs-5">৳{order.grand_total}</div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card p-3">
                <div className="small text-muted">Total Paid</div>
                <div className="fw-bold fs-5 text-success">
                  ৳{order.paid_amount}
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card p-3">
                <div className="small text-muted">Due</div>
                <div className="fw-bold fs-5 text-danger">
                  ৳{order.due_amount}
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card p-3">
                <div className="small text-muted">Status</div>
                <span
                  className={`badge ${status === "Paid"
                      ? "bg-success"
                      : status === "Partial"
                        ? "bg-warning text-dark"
                        : "bg-danger"
                    }`}
                >
                  {status}
                </span>
              </div>
            </div>
          </div>

          {/* Add Payment */}
          {Number(order.due_amount) > 0 && (
            <div className="card mb-3">
              <div className="card-body row g-3">
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={paidInput}
                    onChange={(e) =>
                      this.setState({ paidInput: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Method</label>
                  <select
                    className="form-select"
                    value={method}
                    onChange={(e) =>
                      this.setState({ method: e.target.value })
                    }
                  >
                    <option>Cash</option>
                    <option>Bkash</option>
                    <option>Nagad</option>
                    <option>Card</option>
                    <option>Bank</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Note</label>
                  <input
                    className="form-control"
                    value={note}
                    onChange={(e) =>
                      this.setState({ note: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-2 d-grid">
                  <button
                    className="btn btn-success"
                    disabled={submitting}
                    onClick={this.handleConfirmPayment}
                  >
                    {submitting ? "Saving..." : "Confirm Payment"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment History */}
          <div className="card mb-3">
            <div className="card-header fw-bold">Payment History</div>
            <div className="card-body p-0">
              <table className="table table-bordered small mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Note</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No payments yet
                      </td>
                    </tr>
                  )}

                  {payments.map((p, i) => (
                    <tr key={p.id || i}>
                      <td>{i + 1}</td>
                      <td>{p.payment_datetime}</td>
                      <td>
                        <span
                          className={`badge ${p.payment_status === "paid"
                              ? "bg-success"
                              : "bg-warning text-dark"
                            }`}
                        >
                          {p.payment_status}
                        </span>
                      </td>
                      <td>৳{p.payment_amount}</td>
                      <td>{p.payment_note || "-"}</td>
                      <td>
                        <button className="dropdown-item py-1 text-danger" onClick={() => this.handleDelete(p.id)}><i className="ph ph-trash me-2"></i> Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(OrderPayments);
